/**
 * The Recipes page's working tools: scale a recipe to what the kitchen is making, find recipes by
 * name or ingredient, and the ready-to-print strings for the recipe PDF.
 *
 * Nothing here saves anything. Every function is pure: the saved recipe never changes.
 *
 * Costs follow the database's cost views (v_recipe_version_leaves): a version's unit_cost_cents is
 * for ONE unit of the recipe's yield (one pizza, one dough ball, one gram of sauce) with waste and
 * ingredient yields in it, so the cost of what is being made is that number times the units being
 * made. Each line is priced the same way from its own amount, so the lines add up to that total.
 */

import {
  canConvertUnit,
  formatNumber,
  fromBaseQuantity,
  getUnit,
  nutritionLabel,
  toBaseQuantity,
  type BaseUnit,
  type EnteredMeasure,
  type Nutrients,
} from '@crave/costing';
import {
  formatQty,
  type MeasurableIngredient,
  type MeasureStyle,
} from '../components/MeasureIn.tsx';
import { LOCATION_TIMEZONE } from '../components/ui.tsx';
import type { RecipePdfRecipe } from '../pdf/types.ts';

/* ---------- Shapes (the parts of GET /v1/admin/recipes these tools read) ---------- */

export interface ScaleLine {
  id?: string | undefined;
  ingredient_id: string;
  ingredient?: string | undefined;
  base_unit?: string | undefined;
  /** In the ingredient's base unit (g, ml or each), in `unit`. */
  quantity: number;
  unit: string;
  entry_quantity?: number | null | undefined;
  entry_unit?: string | null | undefined;
  entry_package_id?: string | null | undefined;
  entry_label?: string | null | undefined;
  waste_factor: number;
  is_removable: boolean;
  /**
   * The database's cost breakdown row for this ingredient, per unit of the recipe's yield. It adds up
   * every use of the ingredient, including inside kitchen-made lines, so it is only a fallback.
   * Null for a kitchen-made line (costed through its recipe).
   */
  cost_cents?: number | null | undefined;
}

export interface ScaleVersion {
  id: string;
  version: number;
  status: string;
  published_at?: string | null | undefined;
  unit_cost_cents: number | null;
  has_complete_cost: boolean;
  lines: readonly ScaleLine[] | null;
}

export interface ScaleRecipe {
  id: string;
  name: string;
  kind: string;
  menu_item_name?: string | null | undefined;
  produces_ingredient_id?: string | null | undefined;
  produces_name: string | null;
  yield_qty: number;
  yield_unit: string;
  versions: readonly ScaleVersion[] | null;
}

export interface ScaleIngredient extends MeasurableIngredient {
  id: string;
  name: string;
  base_unit: BaseUnit;
  is_prep?: boolean | undefined;
  /** Percent of what is bought that ends up usable (100 = no trim loss). */
  yield_pct?: number | null | undefined;
  /** What one base unit (g, ml, each) costs, from v_ingredient_unit_cost. Null when unknown. */
  cost_cents_per_base?: number | null | undefined;
}

export interface ScaleContext {
  style: MeasureStyle;
  ingredientsById: ReadonlyMap<string, ScaleIngredient>;
  /** Every recipe, to find the published recipe that makes a kitchen-made ingredient. */
  recipes: readonly ScaleRecipe[];
}

/* ---------- Small helpers ---------- */

const MAX_DEPTH = 8;

export const isPizza = (r: Pick<ScaleRecipe, 'kind'>) => r.kind !== 'PREP';

/** "Dough ball (280 g)" reads as "dough ball" inside a sentence. */
export function plainName(s: string): string {
  const t = s.replace(/\s*\([^)]*\)\s*$/, '').trim() || s;
  return t.charAt(0).toLowerCase() + t.slice(1);
}

/** Money for a person: "$1,234.50", "Under $0.01", "No cost yet". */
export function centsText(c: number | null | undefined): string {
  if (c === null || c === undefined || !Number.isFinite(c)) return 'No cost yet';
  if (c > 0 && c < 0.5) return 'Under $0.01';
  return `$${(Math.round(c) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function baseUnitOf(unit: string): BaseUnit {
  const kind = getUnit(unit)?.kind;
  return kind === 'mass' ? 'g' : kind === 'volume' ? 'ml' : 'each';
}

/** What a recipe's yield is counted in: grams, millilitres or pieces. */
export function yieldDimension(r: Pick<ScaleRecipe, 'yield_unit'>): BaseUnit {
  return baseUnitOf(r.yield_unit);
}

/** How much one saved batch makes, in its base unit: 6 dough balls, 700 g of sauce, 1 pizza. */
export function yieldBase(r: Pick<ScaleRecipe, 'yield_qty' | 'yield_unit'>): number {
  try {
    return toBaseQuantity(Number(r.yield_qty), r.yield_unit, {
      base_unit: baseUnitOf(r.yield_unit),
    });
  } catch {
    return Number.NaN;
  }
}

function lineBase(l: ScaleLine, ing: ScaleIngredient | undefined): number {
  try {
    return toBaseQuantity(Number(l.quantity), l.unit, ing ?? { base_unit: baseUnitOf(l.unit) });
  } catch {
    return Number(l.quantity);
  }
}

/** A saved yield expressed in an ingredient's own base unit (a batch of 6 in dough balls). */
function yieldIn(r: ScaleRecipe, ing: ScaleIngredient): number | null {
  try {
    const n = toBaseQuantity(Number(r.yield_qty), r.yield_unit, ing);
    return n > 0 ? n : null;
  } catch {
    return null;
  }
}

const yieldShare = (ing: ScaleIngredient | undefined) => {
  const pct = Number(ing?.yield_pct);
  return pct > 0 && pct <= 100 ? pct / 100 : 1;
};

export function publishedVersion<V extends ScaleVersion>(r: {
  versions: readonly V[] | null;
}): V | undefined {
  return (r.versions ?? []).find((v) => v.status === 'published');
}

/** The published recipe that makes a kitchen-made ingredient, if it has lines to follow. */
function recipeMaking(ingredientId: string, ctx: ScaleContext): ScaleRecipe | null {
  const ing = ctx.ingredientsById.get(ingredientId);
  if (!ing?.is_prep) return null;
  const r = ctx.recipes.find(
    (x) =>
      x.produces_ingredient_id === ingredientId && (publishedVersion(x)?.lines?.length ?? 0) > 0,
  );
  return r ?? null;
}

/**
 * Plain numbers the way a cook types them: "2", "2.5", "2,5", "1,000", "1/2", "1 1/2".
 * NaN for anything else.
 */
export function parseAmount(text: string): number {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t === '') return Number.NaN;
  let m = /^(\d+) (\d+)\/(\d+)$/.exec(t);
  if (m) return Number(m[3]) > 0 ? Number(m[1]) + Number(m[2]) / Number(m[3]) : Number.NaN;
  m = /^(\d+)\/(\d+)$/.exec(t);
  if (m) return Number(m[2]) > 0 ? Number(m[1]) / Number(m[2]) : Number.NaN;
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(t)) return Number(t.replace(/,/g, ''));
  if (/^\d*[.,]?\d+$|^\d+[.,]$/.test(t)) return Number(t.replace(',', '.'));
  return Number.NaN;
}

/** A number for an input box: at most three decimals, no trailing zeros. */
export const amountText = (n: number) => String(Math.round(n * 1000) / 1000);

/**
 * Round a scaled amount to what a kitchen can measure. Pieces go to a quarter (a half from 10 up);
 * grams and millilitres to a tenth under 10, whole under 1,000, and the nearest 5 above. A real
 * amount never rounds away to nothing.
 */
export function kitchenRound(qty: number, base: BaseUnit): number {
  if (!Number.isFinite(qty) || qty <= 0) return qty;
  const step = base === 'each' ? (qty < 10 ? 0.25 : 0.5) : qty < 10 ? 0.1 : qty < 1000 ? 1 : 5;
  const rounded = Math.round(Number((qty / step).toFixed(6))) * step;
  return Number((rounded > 0 ? rounded : step).toFixed(2));
}

/** Units a cook measures with spoons and cups, shown as kitchen fractions when scaled. */
const SPOONS_AND_CUPS = new Set([
  'pinch',
  'dash',
  'tsp',
  'tbsp',
  'cup',
  'floz',
  'pt',
  'qt',
  'gal',
  'metric_tsp',
  'metric_tbsp',
  'metric_cup',
]);

/**
 * A line as it was typed, scaled and rounded the way its unit is measured: grams, millilitres and
 * pieces like the rest of the page (47 g), spoons and cups as kitchen fractions (1 tbsp + ⅓ tsp),
 * ounces and pounds to the sheets' decimals, and a package to two decimals (1.67 × 28 oz can).
 */
function enteredAmount(
  exact: number,
  factor: number,
  entered: EnteredMeasure,
  target: MeasurableIngredient,
  style: MeasureStyle,
): string {
  const unit = entered.package_id ? null : (entered.unit ?? null);
  if (unit && SPOONS_AND_CUPS.has(unit)) return formatQty(exact, target, 'us_volume');
  const n = Number(entered.quantity) * factor;
  const q =
    unit && getUnit(unit)?.toBase === 1
      ? kitchenRound(n, baseUnitOf(unit))
      : Number(n.toFixed(n < 10 ? 2 : 1));
  return formatQty(exact, target, style, { entered: { ...entered, quantity: q } });
}

/* ---------- How much are you making? ---------- */

/** 'count' (pieces or pizzas), 'batch' (times the saved recipe), or a unit code ('g', 'oz', 'cup'). */
export type ScaleMode = string;

export interface ScaleChoice {
  mode: ScaleMode;
  /** As typed, so "1." and "1 1/2" can be typed. */
  amount: string;
  /** Break kitchen-made lines (dough, sauce, hot honey) into what goes in them. */
  expand: boolean;
  /** Show the saved recipe's amount next to each scaled amount. */
  showSaved: boolean;
  /**
   * Set when switching what the amount is counted in: the exact amount of food behind the rounded
   * number in the box (10 dough balls behind "1.667 batches"), used while the box still shows it.
   */
  exact?: { amount: string; madeBase: number } | undefined;
}

const MASS_MODES: readonly (readonly [string, string, string])[] = [
  ['g', 'gram', 'grams'],
  ['kg', 'kilogram', 'kilograms'],
  ['oz', 'ounce', 'ounces'],
  ['lb', 'pound', 'pounds'],
];
const VOLUME_MODES: readonly (readonly [string, string, string])[] = [
  ['ml', 'ml', 'ml'],
  ['l', 'liter', 'liters'],
  ['floz', 'fl oz', 'fl oz'],
  ['cup', 'cup', 'cups'],
  ['qt', 'quart', 'quarts'],
  ['gal', 'gallon', 'gallons'],
];

/** The saved recipe, as the scaler first shows it. */
export function defaultChoice(r: ScaleRecipe): ScaleChoice {
  return yieldDimension(r) === 'each'
    ? { mode: 'count', amount: amountText(yieldBase(r)), expand: false, showSaved: false }
    : { mode: 'batch', amount: '1', expand: false, showSaved: false };
}

/** "dough balls" for a recipe that makes dough balls; "pieces" when the name won't read. */
function countWord(r: ScaleRecipe, produced: ScaleIngredient | undefined, n: number): string {
  const ing =
    produced && produced.base_unit === 'each'
      ? produced
      : { base_unit: 'each' as const, name: r.produces_name };
  const one = n === 1;
  const word = formatQty(one ? 1 : 2, ing, 'metric').replace(/^[\d.,]+ /, '');
  if (word === 'each') return one ? 'piece' : 'pieces';
  return word;
}

/**
 * What the amount can be counted in. The chosen one is worded for the typed amount ("1 batch",
 * "2 batches"); the others read as plurals, since switching changes the number.
 */
export function scaleModes(
  r: ScaleRecipe,
  produced: ScaleIngredient | undefined,
  amount: string,
  mode?: ScaleMode,
): { value: ScaleMode; label: string }[] {
  const typedOne = parseAmount(amount) === 1;
  const one = (value: ScaleMode) => typedOne && (mode === undefined || mode === value);
  const batch = { value: 'batch', label: one('batch') ? 'batch' : 'batches' };
  const dim = yieldDimension(r);
  if (dim === 'each') {
    const count = {
      value: 'count',
      label: isPizza(r)
        ? one('count')
          ? 'pizza'
          : 'pizzas'
        : countWord(r, produced, one('count') ? 1 : 2),
    };
    // A pizza recipe makes one pizza, so "batches" would only repeat "pizzas".
    return isPizza(r) && yieldBase(r) === 1 ? [count] : [count, batch];
  }
  const units = dim === 'g' ? MASS_MODES : VOLUME_MODES;
  return [batch, ...units.map(([value, s, p]) => ({ value, label: one(value) ? s : p }))];
}

export type ScaleResult =
  { ok: true; factor: number; madeBase: number } | { ok: false; problem: string };

export const MAX_FACTOR = 1000;

/** How many times the saved recipe, and how much that makes in the yield's base unit. */
export function scaleFactor(
  r: ScaleRecipe,
  choice: Pick<ScaleChoice, 'mode' | 'amount' | 'exact'>,
): ScaleResult {
  const yb = yieldBase(r);
  if (!(yb > 0))
    return {
      ok: false,
      problem: "This recipe doesn't say how much it makes, so it can't be scaled.",
    };
  const pinned =
    choice.exact && choice.exact.amount === choice.amount && choice.exact.madeBase > 0
      ? choice.exact.madeBase
      : null;
  const n = parseAmount(choice.amount);
  if (pinned === null && !(n > 0))
    return { ok: false, problem: 'Type an amount more than 0, like 2 or 1.5.' };
  const dim = yieldDimension(r);
  let made: number;
  if (pinned !== null) made = pinned;
  else if (choice.mode === 'batch') made = n * yb;
  else if (choice.mode === 'count' && dim === 'each') made = n;
  else {
    const def = getUnit(choice.mode);
    if (!def || baseUnitOf(choice.mode) !== dim)
      return { ok: false, problem: 'Choose what you are counting.' };
    made = toBaseQuantity(n, choice.mode, { base_unit: dim });
  }
  const factor = made / yb;
  if (factor > MAX_FACTOR)
    return {
      ok: false,
      problem: `That is more than ${formatNumber(MAX_FACTOR, 0)} times the saved recipe. Type a smaller amount.`,
    };
  return { ok: true, factor, madeBase: made };
}

/** Switch what the amount is counted in, keeping the same amount of food ("1 batch" = "700 grams"). */
export function changeMode(r: ScaleRecipe, choice: ScaleChoice, mode: ScaleMode): ScaleChoice {
  const now = scaleFactor(r, choice);
  if (!now.ok) return { ...choice, mode };
  const dim = yieldDimension(r);
  let n: number;
  if (mode === 'batch') n = now.factor;
  else if (mode === 'count') n = now.madeBase;
  else {
    try {
      n = fromBaseQuantity(now.madeBase, mode, { base_unit: dim });
    } catch {
      return { ...choice, mode };
    }
  }
  const amount = amountText(n);
  return { ...choice, mode, amount, exact: { amount, madeBase: now.madeBase } };
}

/** Whether the choice is exactly the saved recipe. */
export function isSavedAmount(r: ScaleRecipe, choice: ScaleChoice): boolean {
  const s = scaleFactor(r, choice);
  return s.ok && Math.abs(s.factor - 1) < 1e-9;
}

/** "Makes 10 dough balls", "Makes 3 pizzas", "Makes 1,400 g of pizza sauce". */
export function makesText(
  r: ScaleRecipe,
  produced: ScaleIngredient | undefined,
  madeBase: number,
  style: MeasureStyle,
): string {
  const dim = yieldDimension(r);
  if (dim === 'each') {
    const n = Math.round(madeBase * 100) / 100;
    if (isPizza(r)) return `Makes ${formatNumber(n, 2)} ${n === 1 ? 'pizza' : 'pizzas'}`;
    const ing =
      produced && produced.base_unit === 'each'
        ? produced
        : { base_unit: 'each' as const, name: r.produces_name };
    const text = formatQty(n, ing, 'metric');
    if (!/ each$/.test(text)) return `Makes ${text}`;
    return `Makes ${formatNumber(n, 2)} × ${r.produces_name ?? r.name}`;
  }
  const target = produced && produced.base_unit === dim ? produced : { base_unit: dim, name: null };
  return `Makes ${formatQty(madeBase, target, style)} of ${plainName(r.produces_name ?? r.name)}`;
}

/** "2.5 times the saved recipe", or null when it adds nothing (the saved amount, or pizzas). */
export function scaleNote(r: ScaleRecipe, factor: number): string | null {
  if (!Number.isFinite(factor) || Math.abs(factor - 1) < 1e-9) return null;
  if (isPizza(r) && yieldDimension(r) === 'each' && yieldBase(r) === 1) return null;
  return `${formatNumber(factor, factor < 10 ? 2 : 1)} times the saved recipe`;
}

/* ---------- Scaled lines ---------- */

export interface ScaledLine {
  /** Stable per line; an expanded line's key starts with the kitchen-made line's key. */
  key: string;
  ingredientId: string;
  name: string;
  /** In the page's Measure in units, rounded for the kitchen. */
  amount: string;
  /** The same line in the saved recipe (for a broken-down line: for one saved batch). */
  savedAmount: string;
  /** The amount in grams when it can be weighed ("1,665 g"), else null. */
  grams: string | null;
  wastePct: number;
  /** "3% (50 g)" or "None". */
  waste: string;
  /** Cost of this line for the amount being made. */
  costCents: number | null;
  /** The kitchen-made ingredient this line was broken down from, or null. */
  expandedFrom: string | null;
  removable: boolean;
}

export interface ScaledVersion {
  factor: number;
  madeBase: number;
  lines: ScaledLine[];
  /** Food cost for the amount being made; null until every ingredient has a cost. */
  costCents: number | null;
}

function enteredOf(l: ScaleLine): EnteredMeasure | undefined {
  if (l.entry_quantity === null || l.entry_quantity === undefined) return undefined;
  if (!l.entry_unit && !l.entry_package_id && !l.entry_label) return undefined;
  return {
    quantity: l.entry_quantity,
    unit: l.entry_unit ?? null,
    package_id: l.entry_package_id ?? null,
    label: l.entry_label ?? null,
  };
}

function gramsText(exact: number, target: MeasurableIngredient, factor: number): string | null {
  if (!Number.isFinite(exact) || !canConvertUnit('g', target)) return null;
  let g: number;
  try {
    g = fromBaseQuantity(exact, 'g', target);
  } catch {
    return null;
  }
  if (!Number.isFinite(g)) return null;
  const shown = factor === 1 ? g : kitchenRound(g, 'g');
  return `${formatNumber(shown < 10 ? Math.round(shown * 10) / 10 : Math.round(shown), 1)} g`;
}

/** Every ingredient inside the published recipe that makes a kitchen-made ingredient, at any depth. */
function idsInside(
  ingredientId: string,
  ctx: ScaleContext,
  seen: ReadonlySet<string>,
  depth: number,
  into: Set<string>,
): void {
  if (depth >= MAX_DEPTH) return;
  const prep = ctx.recipes.find(
    (x) => x.produces_ingredient_id === ingredientId && publishedVersion(x) !== undefined,
  );
  if (!prep || seen.has(prep.id)) return;
  const next = new Set([...seen, prep.id]);
  for (const l of publishedVersion(prep)?.lines ?? []) {
    into.add(l.ingredient_id);
    idsInside(l.ingredient_id, ctx, next, depth + 1, into);
  }
}

/**
 * A line's cost for one unit of its recipe's yield (one pizza, one dough ball, one gram of sauce),
 * priced from this line's own amount, the way the database prices a leaf: amount, plus waste, over
 * the ingredient's yield, over the recipe's yield, times the ingredient's cost per base unit.
 *
 * A kitchen-made line (dough ball, pizza sauce) is priced from the published recipe that makes it.
 *
 * The line's cost_cents from the API is the database's cost breakdown row, which adds up every use
 * of the ingredient in the recipe, including the salt inside the dough. Next to a direct salt line
 * that would count the dough's salt twice, so it is only a fallback, used before ingredient costs
 * have loaded and only when the ingredient is used nowhere else in the recipe.
 */
function costPerYieldUnit(
  l: ScaleLine,
  recipe: ScaleRecipe,
  version: ScaleVersion,
  ing: ScaleIngredient | undefined,
  ctx: ScaleContext,
): number | null {
  const recipeYield = yieldBase(recipe);
  if (!(recipeYield > 0)) return null;
  const prep = recipeMaking(l.ingredient_id, ctx);
  if (prep && ing) {
    const pv = publishedVersion(prep);
    if (!pv || !pv.has_complete_cost || pv.unit_cost_cents === null) return null;
    const producedYield = yieldIn(prep, ing);
    if (producedYield === null) return null;
    const prepUnitsPerIngredientUnit = yieldBase(prep) / producedYield;
    const used =
      ((lineBase(l, ing) * (1 + l.waste_factor)) / yieldShare(ing)) * prepUnitsPerIngredientUnit;
    return (used / recipeYield) * pv.unit_cost_cents;
  }
  if (ing && ing.cost_cents_per_base !== undefined) {
    const perBase = ing.cost_cents_per_base;
    if (perBase === null || !Number.isFinite(Number(perBase))) return null;
    const used = (lineBase(l, ing) * (1 + Number(l.waste_factor))) / yieldShare(ing);
    return (used / recipeYield) * Number(perBase);
  }
  if (typeof l.cost_cents !== 'number' || !Number.isFinite(l.cost_cents)) return null;
  const inside = new Set<string>();
  for (const other of version.lines ?? [])
    idsInside(other.ingredient_id, ctx, new Set([recipe.id]), 0, inside);
  return inside.has(l.ingredient_id) ? null : l.cost_cents;
}

type RawLine = Omit<ScaledLine, 'savedAmount'>;

function linesFor(
  recipe: ScaleRecipe,
  version: ScaleVersion,
  factor: number,
  expand: boolean,
  ctx: ScaleContext,
  depth: number,
  from: string | null,
  keyPrefix: string,
  seen: ReadonlySet<string>,
): RawLine[] {
  const out: RawLine[] = [];
  const units = factor * yieldBase(recipe);
  for (const [i, l] of (version.lines ?? []).entries()) {
    const ing = ctx.ingredientsById.get(l.ingredient_id);
    const name = l.ingredient ?? ing?.name ?? 'Ingredient';
    const base = lineBase(l, ing);
    const key = `${keyPrefix}${l.id ?? `${l.ingredient_id}-${i}`}`;

    if (expand && ing && depth < MAX_DEPTH) {
      const prep = recipeMaking(l.ingredient_id, ctx);
      const pv = prep ? publishedVersion(prep) : undefined;
      const producedYield = prep ? yieldIn(prep, ing) : null;
      if (prep && pv && producedYield !== null && !seen.has(prep.id)) {
        // Make enough to cover this line's waste and trim, like the food cost does.
        const needed = (base * factor * (1 + l.waste_factor)) / yieldShare(ing);
        out.push(
          ...linesFor(
            prep,
            pv,
            needed / producedYield,
            true,
            ctx,
            depth + 1,
            name,
            `${key}/`,
            new Set([...seen, prep.id]),
          ),
        );
        continue;
      }
    }

    const target: MeasurableIngredient = ing ?? {
      base_unit: (l.base_unit as BaseUnit | undefined) ?? baseUnitOf(l.unit),
      name: l.ingredient ?? null,
    };
    const exact = base * factor;
    const qty = factor === 1 ? exact : kitchenRound(exact, target.base_unit);
    const entered = enteredOf(l);
    const amount =
      ctx.style === 'entered' && entered && factor !== 1
        ? enteredAmount(exact, factor, entered, target, ctx.style)
        : formatQty(qty, target, ctx.style, { entered });
    const wastePct = Number(l.waste_factor) || 0;
    const per = costPerYieldUnit(l, recipe, version, ing, ctx);
    out.push({
      key,
      ingredientId: l.ingredient_id,
      name,
      amount,
      grams: gramsText(exact, target, factor),
      wastePct,
      waste:
        wastePct > 0
          ? `${formatNumber(wastePct * 100, 1)}% (${formatQty(exact * wastePct, target, ctx.style)})`
          : 'None',
      costCents: per === null ? null : per * units,
      expandedFrom: from,
      removable: depth === 0 && l.is_removable,
    });
  }
  return out;
}

/**
 * Every line of a version at the chosen amount, in the page's units, with the saved amounts beside
 * them. With `expand`, kitchen-made lines become the raw ingredients their published recipes need.
 */
export function scaleVersion(
  recipe: ScaleRecipe,
  version: ScaleVersion,
  scale: { factor: number; madeBase: number },
  expand: boolean,
  ctx: ScaleContext,
): ScaledVersion {
  const seen = new Set([recipe.id]);
  const lines = linesFor(recipe, version, scale.factor, expand, ctx, 0, null, '', seen);
  const saved =
    scale.factor === 1 ? lines : linesFor(recipe, version, 1, expand, ctx, 0, null, '', seen);
  const savedByKey = new Map(saved.map((l) => [l.key, l.amount]));
  const costCents =
    version.has_complete_cost && version.unit_cost_cents !== null
      ? version.unit_cost_cents * scale.madeBase
      : null;
  return {
    factor: scale.factor,
    madeBase: scale.madeBase,
    lines: lines.map((l) => ({ ...l, savedAmount: savedByKey.get(l.key) ?? l.amount })),
    costCents,
  };
}

/** Names of the kitchen-made lines that can be broken down (Dough ball, Pizza sauce). */
export function expandableNames(version: ScaleVersion | undefined, ctx: ScaleContext): string[] {
  const names: string[] = [];
  for (const l of version?.lines ?? []) {
    if (recipeMaking(l.ingredient_id, ctx))
      names.push(l.ingredient ?? ctx.ingredientsById.get(l.ingredient_id)?.name ?? 'Ingredient');
  }
  return names;
}

/* ---------- Search ---------- */

/** Lowercase without accents, so "arbol" finds "árbol". */
function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function queryWords(query: string): string[] {
  return fold(query).split(/\s+/).filter(Boolean);
}

/** The text split into matched and unmatched parts, for highlighting. */
export function highlightParts(
  text: string,
  words: readonly string[],
): { text: string; hit: boolean }[] {
  if (words.length === 0 || text === '') return [{ text, hit: false }];
  // Fold one character at a time so positions in the folded text map back to the original.
  const chars = Array.from(text);
  let folded = '';
  const owner: number[] = [];
  chars.forEach((c, i) => {
    const f = fold(c);
    folded += f;
    for (let k = 0; k < f.length; k++) owner.push(i);
  });
  const hit = new Array<boolean>(chars.length).fill(false);
  for (const w of words) {
    let at = folded.indexOf(w);
    while (at !== -1) {
      for (let k = at; k < at + w.length; k++) hit[owner[k]!] = true;
      at = folded.indexOf(w, at + 1);
    }
  }
  const parts: { text: string; hit: boolean }[] = [];
  chars.forEach((c, i) => {
    const last = parts[parts.length - 1];
    if (last && last.hit === hit[i]) last.text += c;
    else parts.push({ text: c, hit: hit[i]! });
  });
  return parts;
}

export const textMatches = (text: string, words: readonly string[]) => {
  const f = fold(text);
  return words.some((w) => f.includes(w));
};

export type KindFilter = 'all' | 'pizzas' | 'kitchen';

export interface RecipeSearch {
  query: string;
  kind: KindFilter;
  hasDraft: boolean;
}

export interface RecipeHit<R extends ScaleRecipe> {
  recipe: R;
  /** The recipe's own name (or menu item, or what it makes) matched. */
  nameHit: boolean;
  /** Ingredient ids of lines whose name matched. */
  lineHits: ReadonlySet<string>;
  /** Kitchen-made line id -> the matching ingredients inside it ("Instant dry yeast" in dough). */
  inside: ReadonlyMap<string, string[]>;
}

function namesInside(
  ingredientId: string,
  ctx: ScaleContext,
  words: readonly string[],
  seen: ReadonlySet<string>,
  depth: number,
): string[] {
  const prep = recipeMaking(ingredientId, ctx);
  const pv = prep ? publishedVersion(prep) : undefined;
  if (!prep || !pv || seen.has(prep.id) || depth >= MAX_DEPTH) return [];
  const next = new Set([...seen, prep.id]);
  const found: string[] = [];
  for (const l of pv.lines ?? []) {
    const name = l.ingredient ?? ctx.ingredientsById.get(l.ingredient_id)?.name ?? '';
    if (name && textMatches(name, words)) found.push(name);
    found.push(...namesInside(l.ingredient_id, ctx, words, next, depth + 1));
  }
  return [...new Set(found)];
}

/**
 * Recipes matching every typed word in their name or an ingredient, including ingredients inside
 * their kitchen-made items (a pizza "uses" yeast through its dough). Filters narrow the list.
 */
export function searchRecipes<R extends ScaleRecipe>(
  recipes: readonly R[],
  search: RecipeSearch,
  ctx: ScaleContext,
): RecipeHit<R>[] {
  const words = queryWords(search.query);
  const out: RecipeHit<R>[] = [];
  for (const r of recipes) {
    if (search.kind === 'pizzas' && !isPizza(r)) continue;
    if (search.kind === 'kitchen' && isPizza(r)) continue;
    if (search.hasDraft && !(r.versions ?? []).some((v) => v.status === 'draft')) continue;
    if (words.length === 0) {
      out.push({ recipe: r, nameHit: false, lineHits: new Set(), inside: new Map() });
      continue;
    }
    const names = [r.name, r.menu_item_name ?? '', r.produces_name ?? ''].map(fold).join(' ');
    const lines = (r.versions ?? [])
      .filter((v) => v.status === 'published' || v.status === 'draft')
      .flatMap((v) => v.lines ?? []);
    const haystack: string[] = [names];
    const lineHits = new Set<string>();
    const inside = new Map<string, string[]>();
    for (const l of lines) {
      const name = l.ingredient ?? ctx.ingredientsById.get(l.ingredient_id)?.name ?? '';
      haystack.push(fold(name));
      if (textMatches(name, words)) lineHits.add(l.ingredient_id);
      if (!inside.has(l.ingredient_id)) {
        const found = namesInside(l.ingredient_id, ctx, words, new Set([r.id]), 0);
        if (found.length) {
          inside.set(l.ingredient_id, found);
          haystack.push(...found.map(fold));
        }
      }
    }
    if (!words.every((w) => haystack.some((h) => h.includes(w)))) continue;
    out.push({ recipe: r, nameHit: words.some((w) => names.includes(w)), lineHits, inside });
  }
  return out;
}

/* ---------- Printing ---------- */

/** "Version 3, published Sep 12, 2026" or "Version 4, draft, not live yet". */
export function versionText(v: Pick<ScaleVersion, 'version' | 'status' | 'published_at'>): string {
  if (v.status === 'published') {
    if (!v.published_at) return `Version ${v.version}, live`;
    const date = new Date(v.published_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: LOCATION_TIMEZONE,
    });
    return `Version ${v.version}, published ${date}`;
  }
  if (v.status === 'draft') return `Version ${v.version}, draft, not live yet`;
  return `Version ${v.version}`;
}

/** Nutrition Facts rows for print, rounded like the label: "Less than 5 mg", "12%". */
export function nutritionRows(
  n: Nutrients,
): { label: string; amount: string; dv: string | null }[] {
  return nutritionLabel(n).map((row) => ({
    label: row.label,
    amount: row.amount.text.charAt(0).toUpperCase() + row.amount.text.slice(1),
    dv: row.dailyValuePct === null ? null : `${row.dailyValuePct}%`,
  }));
}

/** Ingredient rows for the PDF: grams only when they add something. */
export function pdfIngredients(lines: readonly ScaledLine[]): RecipePdfRecipe['ingredients'] {
  return lines.map((l) => ({
    name: l.name,
    amount: l.amount,
    grams: l.grams && l.grams !== l.amount ? l.grams : null,
    waste: l.wastePct > 0 ? l.waste : null,
    note: l.removable ? 'Removable' : null,
    expandedFrom: l.expandedFrom,
  }));
}

/** Cost rows for the PDF, for the amount being made. */
export function pdfCostLines(lines: readonly ScaledLine[]): { name: string; cost: string }[] {
  return lines.map((l) => ({ name: l.name, cost: centsText(l.costCents) }));
}
